import useSWR from "swr";
import axios from "axios";
import { useContext, useMemo } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { AirtableEndpointEnum } from "../../../enums/endpoints";
import { AIRTABLE_API_URL } from "../../../utils/env";

export interface IAirtableRecord {
  [address: string]: string;
}

interface IAirtableTagsResponse {
  records: IAirtableRecord;
}

const STORAGE_KEY = "airtable-tags-cache";

const isTagsResponse = (value: unknown): value is IAirtableTagsResponse => {
  if (!value || typeof value !== "object") return false;
  const { records } = value as IAirtableTagsResponse;
  return (
    !!records &&
    typeof records === "object" &&
    !Array.isArray(records) &&
    Object.values(records).every((name) => typeof name === "string")
  );
};

const getCachedData = (): IAirtableTagsResponse | undefined => {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    const data: unknown = cached ? JSON.parse(cached) : undefined;
    return isTagsResponse(data) ? data : undefined;
  } catch {
    return undefined;
  }
};

const saveToCache = (data: IAirtableTagsResponse) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Tags remain available in memory if browser storage is unavailable.
  }
};

export const useAirtableTags = () => {
  const { tokenId } = useContext(AuthContext);
  const endpoint = AIRTABLE_API_URL(AirtableEndpointEnum.ACCOUNT_TAGS);
  const cachedData = useMemo(
    () => (tokenId ? getCachedData() : undefined),
    [tokenId]
  );

  const swr = useSWR<IAirtableTagsResponse>(
    tokenId ? [endpoint, tokenId] : null,
    async () => {
      try {
        const response = await axios.get<unknown>(endpoint, {
          headers: { Authorization: `Bearer ${tokenId}` },
          timeout: 30000,
        });
        if (!isTagsResponse(response.data)) {
          throw new Error("Invalid account tags response");
        }
        saveToCache(response.data);
        return response.data;
      } catch {
        // SWR retains the last successful tags and retries. Do not expose
        // Axios request configuration (which includes the admin credential).
        throw new Error("Unable to load account tags");
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 60000,
      errorRetryCount: 3,
      revalidateOnMount: true,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      fallbackData: cachedData,
    }
  );

  return {
    ...swr,
    data: tokenId ? swr.data : undefined,
  };
};
